import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { DateTime } from 'luxon';
import React, { ReactNode, useCallback, useMemo, useRef } from 'react';
import { ImageTransformOptions, ImageTransformType } from '@diritto/common/imageKit/constants';
import { SectionRectBannerDto } from '@diritto/common/relay/dtos/section-relay.dtos';
import { TrackerExtraInfoProps, TrackingEventType } from '@diritto/common/tracker/tracking-event.constant';
import { setUrlQueryString } from '@diritto/common/utils/urls';
import { SectionSlideButton } from 'app-components/home/sections/components/SectionSlideButton';
import {
  BannerStyles,
  Style,
  StyledBackgroundImage,
  StyledDescription,
  StyledItemWrapper,
  StyledTextWrapper,
  StyledTitle,
} from 'app-components/home/sections/RectBannerStyle';
import { isPC } from 'constants/layout';
import { useAuthState } from 'contexts/AuthProvider';
import { useTrackingState } from 'contexts/TrackingProvider';
import { hideScrollbar } from 'styles/common';
import { ExternalLink } from 'ui-components/common/ExternalLink';
import { Link } from 'ui-components/common/Link';
import { useSlide } from 'ui-hooks/useSlide';

const HorizontalGap = 8;

export const SectionRectBanner = ({
  section,
  trackerExtraInfo,
}: { section: SectionRectBannerDto } & TrackerExtraInfoProps) => {
  const { profile } = useAuthState();
  const { tracker } = useTrackingState();
  const slideRef = useRef<HTMLDivElement>(null);
  const { handlePrevSlide, handleNextSlide, handleSlide, showSlideButton } = useSlide(slideRef, HorizontalGap);
  const style = BannerStyles[section.design];

  const handleClick = useCallback(
    (index: number) => {
      tracker.track(TrackingEventType.ClickItemOnHomeSection, {
        sectionId: section.id,
        sectionDesign: section.design,
        listItemIndex: index,
        trackerExtraInfo,
      });
    },
    [section.design, section.id, tracker, trackerExtraInfo],
  );

  const getUrl = useCallback(
    (link: string) =>
      link.includes('typeform.com')
        ? setUrlQueryString(link, 'profile_id', profile?.id ? profile.id.toString() : '')
        : link,
    [profile?.id],
  );

  const filteredBanners = useMemo(() => {
    const now = DateTime.now();
    return section.banners.filter(
      banner => now >= DateTime.fromISO(`${banner.startAt}`) && now <= DateTime.fromISO(`${banner.endAt}`),
    );
  }, [section.banners]);

  if (filteredBanners.length === 0) {
    return null;
  }

  return (
    <StyledWrapper size={style}>
      <SectionSlideButton
        showSlideButton={showSlideButton}
        handleNextSlide={handleNextSlide}
        handlePrevSlide={handlePrevSlide}
      />
      <StyledScrollContainer ref={slideRef} onScroll={handleSlide} rowCount={1} size={style}>
        {filteredBanners.map((banner, index) => (
          <StyledItemWrapper key={`${section.title}_${banner.file.id}_${banner.title}`} bannerStyle={style}>
            <BannerWrapper
              key={`${banner.file}-${banner.title}`}
              isExternal={banner.isExternal}
              url={getUrl(banner.link)}
              onClick={() => handleClick(index)}>
              <StyledBackgroundImage
                bannerStyle={style}
                transform={{
                  path: banner.file.path,
                  isPrivate: banner.file.isPrivate,
                  transformation: ImageTransformOptions[ImageTransformType.Banner],
                }}
              />
              <StyledTextWrapper bannerStyle={style}>
                <StyledTitle bannerStyle={style}>{banner.title}</StyledTitle>
                <StyledDescription bannerStyle={style}>{banner.description}</StyledDescription>
              </StyledTextWrapper>
            </BannerWrapper>
          </StyledItemWrapper>
        ))}
      </StyledScrollContainer>
    </StyledWrapper>
  );
};

const BannerWrapper = ({
  children,
  isExternal,
  onClick,
  url,
}: {
  isExternal: boolean;
  children: ReactNode;
  onClick?: any;
  url: string;
}) =>
  isExternal ? (
    <ExternalLink href={url} onClick={onClick}>
      {children}
    </ExternalLink>
  ) : (
    <Link href={url} onClick={onClick}>
      {children}
    </Link>
  );

const StyledWrapper = styled.div<{ size: Style }>`
  position: relative;
  display: flex;
  flex-direction: row;
  height: ${props => props.size.height}px;
`;

const StyledScrollContainer = styled.div(
  ({ size }: { rowCount?: number; size: Style }) => css`
    position: relative;
    display: flex;
    flex-direction: column;
    flex-flow: column wrap;
    align-content: flex-start;
    width: 100%;
    padding: 0 16px;
    overflow: hidden;
    ${hideScrollbar};
    gap: ${HorizontalGap}px;
    height: ${size.height}px;
    overflow-x: scroll;
    scroll-snap-type: x mandatory;

    @media (${isPC}) {
      padding: 0;
      margin: 0 15px;
      width: 700px;
      scroll-snap-type: x;
    }
  `,
);
